import { searchStaffWithGuestRegistration } from "apis";
import { searchGuestProfile } from "apis/guest-profile.api";
import {
  FilterBar,
  FilterDateRange,
  FilterMultiSelect,
  FilterSingleSelect,
  FilterTimeRange,
} from "components/commons";
import { useApi, useMount } from "hooks";
import {
  guestProfileFilterRequest,
  guestProfileFilterResponse,
  staffWithGuestFilterRequest,
  staffWithTransactionFilterResponse,
} from "mappers";
import React, { useCallback, useMemo } from "react";
import { mapObject, mapObjectsToSelect } from "services";
import { statusFilter } from "./filters";
import lang from "translations";

const GuestFilter = ({
  filterState,
  requestState,
  modifyFilter,
  modifyFilters,
  clearFilter,
  fetchGuests,
  applyFilter,
}) => {
  const {
    request: searchStaffRequest,
    loading: loadingStaff,
    mappedData: staffs,
  } = useApi({
    api: searchStaffWithGuestRegistration,
    isArray: true,
    mapper: staffWithTransactionFilterResponse,
  });

  const {
    request: searchGuestProfileRequest,
    loading: loadingGuestProfile,
    mappedData: guestProfiles,
  } = useApi({
    api: searchGuestProfile,
    isArray: true,
    params: {
      includeDeleted: true,
    },
    mapper: guestProfileFilterResponse,
  });

  useMount(() => {
    fetchStaffRequest(requestState);
    fetchGuestProfileRequest(requestState);
  });

  const fetchStaffRequest = useCallback(
    (requestState) => {
      searchStaffRequest(mapObject({ ...requestState }, staffWithGuestFilterRequest));
    },
    [searchStaffRequest]
  );

  const fetchGuestProfileRequest = useCallback(
    (requestState) => {
      searchGuestProfileRequest(mapObject({ ...requestState }, guestProfileFilterRequest));
    },
    [searchGuestProfileRequest]
  );

  const applyFilterCb = useCallback(
    async (searchKey) => {
      await applyFilter(searchKey);
    },
    [applyFilter]
  );

  const clearFilterCb = useCallback(() => {
    clearFilter();
  }, [clearFilter]);

  const changeDateRangeCb = useCallback(
    (name, value) => {
      const { requestState } = modifyFilter(name, { value });
      fetchStaffRequest(requestState);
    },
    [fetchStaffRequest, modifyFilter]
  );

  const staffOptions = useMemo(() => {
    return mapObjectsToSelect(staffs, { valueKey: "id", textKey: "name" });
  }, [staffs]);

  const guestProfileOptions = useMemo(() => {
    const filteredGuestProfile = [
      ...new Map(guestProfiles.map((guest) => [guest["id"], guest])).values(),
    ];
    return mapObjectsToSelect(filteredGuestProfile, { valueKey: "id", textKey: "name" });
  }, [guestProfiles]);

  return (
    <FilterBar
      placeholder={lang.searchGuestsByNameOrId}
      className="mb-lg"
      onClear={clearFilterCb}
      onApply={applyFilterCb}
      filterState={filterState}
    >
      <FilterDateRange
        id="dateRange"
        label={lang.dateOfRegistration}
        value={filterState.dateRange}
        onChange={(value) => changeDateRangeCb("dateRange", value)}
      />
      <FilterTimeRange
        id="timeRange"
        label={lang.timeRange}
        value={filterState.dateRange}
        onChange={(value) => modifyFilter("dateRange", { value })}
      />
      <FilterSingleSelect
        id="guestTagStatus"
        label={lang.guestOrTagStatus}
        summaryFallback={lang.all}
        options={statusFilter}
        value={filterState.guestTagStatus}
        onChange={(value) => modifyFilter("guestTagStatus", { value })}
      />
      <FilterMultiSelect
        id="staffs"
        label={lang.staff}
        allLabel={staffOptions.length ? lang.allStaff : lang.noAvailableStaff}
        loading={loadingStaff}
        options={staffOptions}
        value={filterState.staffs}
        onChange={(value) => modifyFilter("staffs", { value })}
      />
      <FilterMultiSelect
        id="guestProfiles"
        label={lang.guestProfile}
        allLabel={
          guestProfileOptions.length ? lang.allGuestProfiles : lang.noAvailableGuestProfile
        }
        loading={loadingGuestProfile}
        options={guestProfileOptions}
        value={filterState.guestProfiles}
        onChange={(value) => modifyFilter("guestProfiles", { value })}
      />
      {/* <Field className="col-span-4" filterLabel={lang.creditStatus}>
        <Select
          name="creditStatus"
          options={creditFilter}
          value={filterState.creditStatus}
          onChange={modifyFilter}
        />
      </Field> */}
    </FilterBar>
  );
};

export default GuestFilter;
